import React, { useState, useEffect } from 'react';
import { FileText, Database, Settings, CheckCircle, AlertCircle, Building, Calendar, Columns } from 'lucide-react';
import QueryBuilderModal from '../../components/QueryBuilderModal';

const EXPORT_FORMATS = {
    excel: { name: 'Excel (.xlsx)', icon: FileText, color: 'green' },
    csv: { name: 'CSV (.csv)', icon: FileText, color: 'blue' },
    json: { name: 'JSON (.json)', icon: Database, color: 'purple' }
};

const PREDEFINED_EXPORTS = {
    dealer_locations: {
        name: 'Dealer Locations',
        description: 'Complete dealer location data in import format',
        icon: Building,
        endpoint: '/api/admin/exports/dealer-locations',
        columns: [
            'group_name', 'location_name', 'trading_name', 'marketing_contact', 'site_phone_number',
            'building_name', 'building_number', 'street_name', 'locality', 'town_city',
            'county', 'postcode', 'country', 'address_type'
        ]
    },
    contact_plans: {
        name: 'Contact Plans',
        description: 'All contact plan messages configured for dealerships',
        icon: Calendar,
        endpoint: '/api/admin/exports/contact-plans',
        columns: [
            'dealership_name', 'location_name', 'message_type', 'message_name', 'message_content',
            'send_when', 'feed', 'notes', 'query_mode', 'natural_query', 'sql_query', 'query_description',
            'default_send_when', 'default_feed', 'created_at', 'updated_at'
        ]
    }
};

const AdminDataExportsPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Custom export state
    const [showCustomExport, setShowCustomExport] = useState(false);
    const [showQueryBuilder, setShowQueryBuilder] = useState(false);
    const [customQuery, setCustomQuery] = useState({});
    const [customExportName, setCustomExportName] = useState('');
    const [availableTables, setAvailableTables] = useState([]);

    useEffect(() => {
        loadAvailableTables();
    }, []);

    const loadAvailableTables = async () => {
        try {
            const response = await fetch('/api/admin/exports/schema/tables');
            const result = await response.json();
            if (response.ok) {
                setAvailableTables(result || []);
            }
        } catch (error) {
            console.error('Failed to load available tables:', error);
        }
    };

    const handleExport = async (exportType, format) => {
        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const exportConfig = PREDEFINED_EXPORTS[exportType];
            const response = await fetch(`${exportConfig.endpoint}?format=${format}`, {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error(`Export failed: ${response.status} ${response.statusText}`);
            }

            // Handle file download
            const contentDisposition = response.headers.get('content-disposition');
            let filename = `${exportConfig.name.toLowerCase().replace(/\s+/g, '_')}.${format}`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSuccess(`Successfully exported ${exportConfig.name} as ${format.toUpperCase()}`);

        } catch (error) {
            console.error('Export error:', error);
            setError(`Export failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCustomExport = async (format) => {
        if (!customQuery.sqlQuery) {
            setError('Please create a custom query first');
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch('/api/admin/exports/custom', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sql: customQuery.sqlQuery,
                    format: format,
                    filename: customExportName || 'custom_export'
                })
            });

            if (!response.ok) {
                throw new Error(`Custom export failed: ${response.status} ${response.statusText}`);
            }

            // Handle file download
            const contentDisposition = response.headers.get('content-disposition');
            let filename = `${customExportName || 'custom_export'}.${format}`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSuccess(`Successfully exported custom data as ${format.toUpperCase()}`);

        } catch (error) {
            console.error('Custom export error:', error);
            setError(`Custom export failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuerySave = (queryData) => {
        setCustomQuery(queryData);
        setShowQueryBuilder(false);
    };

    const renderExportButtons = (exportType) => (
        <div className="flex gap-2 mt-4">
            {Object.entries(EXPORT_FORMATS).map(([format, config]) => (
                <button
                    key={format}
                    onClick={() => handleExport(exportType, format)}
                    disabled={isLoading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-${config.color}-600 hover:bg-${config.color}-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors`}
                >
                    <config.icon size={16} />
                    {config.name}
                </button>
            ))}
        </div>
    );

    const renderCustomExportButtons = () => (
        <div className="flex gap-2 mt-4">
            {Object.entries(EXPORT_FORMATS).map(([format, config]) => (
                <button
                    key={format}
                    onClick={() => handleCustomExport(format)}
                    disabled={isLoading || !customQuery.sqlQuery}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-${config.color}-600 hover:bg-${config.color}-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors`}
                >
                    <config.icon size={16} />
                    {config.name}
                </button>
            ))}
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Data Exports</h1>
                <p className="text-gray-600 mt-2">
                    Export your data in multiple formats: Excel, CSV, or JSON
                </p>
            </div>

            {/* Status Messages */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="text-red-600" size={20} />
                    <div>
                        <h4 className="text-red-800 font-medium">Export Error</h4>
                        <p className="text-red-700">{error}</p>
                    </div>
                </div>
            )}

            {success && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="text-green-600" size={20} />
                    <div>
                        <h4 className="text-green-800 font-medium">Export Successful</h4>
                        <p className="text-green-700">{success}</p>
                    </div>
                </div>
            )}

            {/* Predefined Exports */}
            <div className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Predefined Exports</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(PREDEFINED_EXPORTS).map(([key, exportConfig]) => {
                        const Icon = exportConfig.icon;
                        return (
                            <div key={key} className="bg-white border border-gray-200 rounded-lg p-6">
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                        <Icon className="text-blue-600" size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-900">{exportConfig.name}</h3>
                                        <p className="text-gray-600 mt-1">{exportConfig.description}</p>
                                        <div className="mt-3">
                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Included Columns:</h4>
                                            <div className="flex flex-wrap gap-1">
                                                {exportConfig.columns.slice(0, 6).map((column) => (
                                                    <span key={column} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                                        {column}
                                                    </span>
                                                ))}
                                                {exportConfig.columns.length > 6 && (
                                                    <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                                                        +{exportConfig.columns.length - 6} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {renderExportButtons(key)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Custom Export Section */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
                            <Columns className="text-purple-600" size={24} />
                            Custom Export
                        </h2>
                        <p className="text-gray-600 mt-1">Create custom queries to export specific data from the onboarding_db database</p>
                    </div>
                    <button
                        onClick={() => setShowCustomExport(!showCustomExport)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                        {showCustomExport ? 'Hide' : 'Show'} Custom Export
                    </button>
                </div>

                {showCustomExport && (
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                        {/* Available Tables Info */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Available Tables
                            </label>
                            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                    {availableTables.map((table) => (
                                        <span key={table.table_name} className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                                            {table.table_name}
                                        </span>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    These tables are available in the onboarding_db database for your custom queries.
                                </p>
                            </div>
                        </div>

                        {/* Export Name */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Export Filename (optional)
                            </label>
                            <input
                                type="text"
                                value={customExportName}
                                onChange={(e) => setCustomExportName(e.target.value)}
                                placeholder="custom_export"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                        </div>

                        {/* Query Builder */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-medium text-gray-700">
                                    Query Configuration
                                </label>
                                <button
                                    onClick={() => setShowQueryBuilder(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    <Settings size={16} />
                                    Open Query Builder
                                </button>
                            </div>

                            {customQuery.sqlQuery ? (
                                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Query:</h4>
                                    <pre className="text-sm text-gray-800 bg-white p-3 rounded border overflow-x-auto">
                                        {customQuery.sqlQuery}
                                    </pre>
                                    {customQuery.description && (
                                        <p className="text-sm text-gray-600 mt-2">
                                            <strong>Description:</strong> {customQuery.description}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-center">
                                    <p className="text-gray-500">No query configured. Use the Query Builder to create your custom export query.</p>
                                </div>
                            )}
                        </div>

                        {/* Export Buttons */}
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Export Formats:</h4>
                            {renderCustomExportButtons()}
                        </div>
                    </div>
                )}
            </div>

            {/* Query Builder Modal */}
            {showQueryBuilder && (
                <QueryBuilderModal
                    isOpen={showQueryBuilder}
                    onClose={() => setShowQueryBuilder(false)}
                    onSave={handleQuerySave}
                    initialData={customQuery}
                />
            )}
        </div>
    );
};

export default AdminDataExportsPage;